/**
 * QuantXAnalyticsPanel
 *
 * Main container component for QuantX Analytics display.
 * Uses DUAL-WIDGET architecture for scalability (15+ indicators).
 * Widget 1: Market Context - "Where is the market?"
 * Widget 2: Action Center - "What should I do?"
 *
 * @see docs/HANDOFF_QuantX_Integration.md
 * @see docs/UI_SCALABLE_ARCHITECTURE.md
 */

import React, { useState, memo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    CircularProgress,
    Chip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import useQuantXData from '../../hooks/useQuantXData';
import { useSignalRContext } from '../../SignalR/useSignalR';
import { MarketContextWidget, ActionCenterWidget } from './widgets';

/**
 * Trend direction indicator - Institutional Grade Styling
 */
const TrendIndicator = ({ trend, score }) => {
    const getTrendColor = (trend) => {
        switch (trend?.toUpperCase()) {
            case 'STRONG_UPTREND':
                return '#22c55e';
            case 'UPTREND':
                return '#4ade80';
            case 'STRONG_DOWNTREND':
                return '#ef4444';
            case 'DOWNTREND':
                return '#f87171';
            case 'RANGING':
            case 'SIDEWAYS':
                return '#fbbf24';
            default:
                return '#9ca3af';
        }
    };

    const getTrendArrow = (trend) => {
        if (trend?.includes('UP')) return '↑';
        if (trend?.includes('DOWN')) return '↓';
        return '→';
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
                sx={{
                    color: getTrendColor(trend),
                    fontWeight: 700,
                    fontSize: '11px',
                    fontFamily: 'monospace'
                }}
            >
                {getTrendArrow(trend)} {trend?.replace('_', ' ') || 'N/A'}
            </Typography>
            {score !== undefined && (
                <Chip
                    label={`${score}/100`}
                    size="small"
                    sx={{
                        backgroundColor: getTrendColor(trend),
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '9px',
                        height: '16px',
                        '& .MuiChip-label': { px: 0.75 }
                    }}
                />
            )}
        </Box>
    );
};

/**
 * QuantXAnalyticsPanel Component - Dual Widget Layout
 */
const QuantXAnalyticsPanel = memo(({
    symbol = 'XAUUSD_ct',
    token,
    autoRefresh = true,
    refreshInterval = 30000,
    collapsible = true,
    defaultCollapsed = false,
    strategyId = null,
    layout = 'stacked' // 'stacked' | 'side-by-side'
}) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

    // Track individual widget collapse states
    const [widgetStates, setWidgetStates] = useState({
        marketContext: false,
        actionCenter: false
    });

    // Get positions from SignalR context for summary bar
    const { positionsList = [] } = useSignalRContext() || {};

    // Fetch QuantX data with SignalR real-time updates
    const {
        currentState,
        strategySignal,
        strategyDashboard,
        isLoading,
        isRefreshing,
        hasData,
        hasError,
        error,
        trendScore,
        overallTrend,
        signal,
        confidence,
        session,
        signalRConnected,
        signalRState,
        refresh,
        reconnectSignalR
    } = useQuantXData({
        symbol,
        token,
        autoRefresh,
        refreshInterval,
        enabled: !!token && !!symbol,
        useSignalR: true
    });

    const handleToggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleWidgetToggle = (widgetId) => {
        setWidgetStates(prev => ({
            ...prev,
            [widgetId]: !prev[widgetId]
        }));
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: isCollapsed ? 'auto' : '100%',
                minHeight: isCollapsed ? 'auto' : '200px',
                maxHeight: 'calc(100vh - 140px)',
                gap: 1
            }}
        >
            {/* Main Header - Compact Institutional Grade */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    padding: '5px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    flexShrink: 0
                }}
            >
                {/* Left: Title + Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 800,
                            color: '#ef4444',
                            letterSpacing: '0.5px',
                            fontFamily: 'monospace'
                        }}
                    >
                        QuantX
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: '#b4bcd0',
                            fontFamily: 'monospace',
                            fontWeight: 500
                        }}
                    >
                        {symbol}
                    </Typography>

                    {hasData && (
                        <>
                            <Box sx={{ width: '1px', height: '14px', backgroundColor: '#4b5563' }} />
                            <TrendIndicator trend={overallTrend} score={trendScore} />
                        </>
                    )}
                </Box>

                {/* Right: Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* SignalR Connection Status */}
                    <Tooltip title={signalRConnected ? 'Real-time connected' : `SignalR: ${signalRState}`} arrow>
                        <Box
                            sx={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: signalRConnected ? '#22c55e' : signalRState === 'connecting' ? '#fbbf24' : '#ef4444',
                                boxShadow: signalRConnected ? '0 0 6px #22c55e' : 'none',
                                cursor: 'pointer'
                            }}
                            onClick={!signalRConnected ? reconnectSignalR : undefined}
                        />
                    </Tooltip>

                    {/* Session */}
                    {hasData && session && (
                        <Chip
                            label={session}
                            size="small"
                            sx={{
                                backgroundColor: '#262626',
                                color: '#fbbf24',
                                fontSize: '8px',
                                height: '14px',
                                fontWeight: 700
                            }}
                        />
                    )}

                    {/* Refresh Button */}
                    <Tooltip title="Refresh Data" arrow>
                        <IconButton
                            onClick={refresh}
                            size="small"
                            disabled={isLoading}
                            sx={{
                                color: isRefreshing ? '#fbbf24' : '#9ca3af',
                                padding: '2px',
                                '&:hover': { color: '#22c55e' }
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 14, animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Collapse All Button */}
                    {collapsible && (
                        <Tooltip title={isCollapsed ? 'Expand All' : 'Collapse All'} arrow>
                            <IconButton
                                onClick={handleToggleCollapse}
                                size="small"
                                sx={{ color: '#9ca3af', padding: '2px' }}
                            >
                                {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ExpandLessIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Content - Dual Widgets */}
            {!isCollapsed && (
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: layout === 'side-by-side' ? 'row' : 'column',
                        gap: 1
                    }}
                >
                    {/* Loading State */}
                    {isLoading && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '120px',
                                gap: 1,
                                width: '100%'
                            }}
                        >
                            <CircularProgress size={24} sx={{ color: '#ef4444' }} />
                            <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                                Loading QuantX data...
                            </Typography>
                        </Box>
                    )}

                    {/* Error State */}
                    {hasError && !isLoading && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '120px',
                                gap: 1,
                                width: '100%'
                            }}
                        >
                            <Typography sx={{ fontSize: '12px', color: '#ef4444', fontWeight: 700 }}>
                                Failed to load data
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#9ca3af' }}>
                                {error?.message || 'Unknown error'}
                            </Typography>
                            <IconButton onClick={refresh} size="small" sx={{ color: '#ef4444' }}>
                                <RefreshIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                    )}

                    {/* Dual Widget Display */}
                    {hasData && !isLoading && (
                        <>
                            {/* WIDGET 1: Market Context */}
                            <Box sx={{ flex: layout === 'side-by-side' ? 1 : 'none' }}>
                                <MarketContextWidget
                                    strategyDashboard={strategyDashboard}
                                    currentState={currentState}
                                    trendScore={trendScore}
                                    overallTrend={overallTrend}
                                    isCollapsed={widgetStates.marketContext}
                                    onToggleCollapse={() => handleWidgetToggle('marketContext')}
                                    collapsible={true}
                                />
                            </Box>

                            {/* WIDGET 2: Action Center */}
                            <Box sx={{ flex: layout === 'side-by-side' ? 1 : 'none' }}>
                                <ActionCenterWidget
                                    strategySignal={strategySignal}
                                    currentState={currentState}
                                    signal={signal}
                                    confidence={confidence}
                                    positions={positionsList}
                                    strategyId={strategyId}
                                    isCollapsed={widgetStates.actionCenter}
                                    onToggleCollapse={() => handleWidgetToggle('actionCenter')}
                                    collapsible={true}
                                />
                            </Box>
                        </>
                    )}

                    {/* No Data State */}
                    {!hasData && !isLoading && !hasError && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '120px',
                                gap: 1,
                                width: '100%'
                            }}
                        >
                            <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                                No data available for {symbol}
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>
                                Check if DataFeed Server is running
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* CSS for spinner animation */}
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
        </Box>
    );
});

QuantXAnalyticsPanel.displayName = 'QuantXAnalyticsPanel';

export default QuantXAnalyticsPanel;
